import { useCallback, useState } from 'react';
import { AssetLoadError, analyseBuffer } from '../../audio/assets/bufferCache';
import { AudioFileError, validateImport } from '../../audio/assets/formats';
import type { Recording } from '../../audio/recorder';
import * as audioOps from '../../domain/audioOps';
import { MAX_SAMPLE_BYTES, type Asset } from '../../domain/schemas';
import { createId, nowIso } from '../../lib/ids';
import { useProject } from '../../state/projectContext';
import { useServices } from '../../state/services';
import { StorageError, toStorageError } from '../../storage/errors';
import { withAssetRemoved } from '../../storage/projectRepository';

export interface LibraryActions {
  busy: boolean;
  error: string | null;
  errorDetails: string[];
  clearError(): void;
  importFiles(files: readonly File[]): Promise<string[]>;
  saveRecording(recording: Recording, name: string): Promise<string | null>;
  remove(assetId: string, detach: boolean): Promise<boolean>;
  /** Decodes an older asset that has no stored analysis yet and records it. */
  analyse(assetId: string): Promise<boolean>;
}

const describe = (name: string, error: unknown): { message: string; details: string[] } => {
  if (error instanceof AudioFileError) return { message: error.message, details: [] };
  if (error instanceof AssetLoadError) return { message: `“${name}”: ${error.message}`, details: [] };
  const storage = error instanceof StorageError ? error : toStorageError(error, `“${name}” could not be saved.`);
  return { message: storage.message, details: storage.details };
};

/**
 * Library operations shared by the sample library and the recorder. Every write first
 * flushes pending edits, then goes through one atomic repository transaction, so the
 * project document and the stored file can never disagree.
 */
export function useAudioLibrary(): LibraryActions {
  const { session, update } = useProject();
  const { repo, buffers } = useServices();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; details: string[] } | null>(null);

  const store = useCallback(
    async (asset: Omit<Asset, 'audio'>, data: ArrayBuffer): Promise<void> => {
      // Decode once up front: rejects undecodable files before anything is stored,
      // and the decoded audio stays cached for the first playback.
      const decoded = await buffers.adopt(asset.id, data);
      const audio = analyseBuffer(decoded);
      try {
        if (!(await session.saveNow())) throw new StorageError('transaction', 'Save the pending changes first (see the save status above).');
        const current = session.snapshot.project!;
        const saved = await repo.addAsset(current, { ...asset, audio }, data);
        update((p) => ({ ...p, assets: saved.assets }));
      } catch (e) {
        buffers.invalidate(asset.id);
        throw e;
      }
    },
    [buffers, repo, session, update],
  );

  const importFiles = useCallback(
    async (files: readonly File[]) => {
      setBusy(true);
      setError(null);
      const added: string[] = [];
      try {
        for (const file of files) {
          try {
            const header = new Uint8Array(await file.slice(0, 16).arrayBuffer());
            const { mimeType } = validateImport(file.name, file.size, header);
            const data = await file.arrayBuffer();
            const id = createId('ast');
            await store(
              { id, name: file.name.slice(0, 200) || 'sample', kind: 'audio', mimeType, sizeBytes: data.byteLength, source: 'local-file', createdAt: nowIso() },
              data,
            );
            added.push(id);
          } catch (e) {
            setError(describe(file.name, e));
            break;
          }
        }
      } finally {
        setBusy(false);
      }
      return added;
    },
    [store],
  );

  const saveRecording = useCallback(
    async (recording: Recording, name: string) => {
      setBusy(true);
      setError(null);
      const label = name.trim().slice(0, 200) || 'Recording';
      try {
        if (recording.blob.size > MAX_SAMPLE_BYTES) throw new AudioFileError('too-large', 'The recording is larger than the 20 MB limit.');
        const data = await recording.blob.arrayBuffer();
        const id = createId('ast');
        await store(
          { id, name: label, kind: 'audio', mimeType: recording.mimeType, sizeBytes: data.byteLength, source: 'recording', createdAt: nowIso() },
          data,
        );
        return id;
      } catch (e) {
        setError(describe(label, e));
        return null;
      } finally {
        setBusy(false);
      }
    },
    [store],
  );

  const remove = useCallback(
    async (assetId: string, detach: boolean) => {
      setBusy(true);
      setError(null);
      try {
        if (!(await session.saveNow())) throw new StorageError('transaction', 'Save the pending changes first.');
        const saved = await repo.removeAsset(session.snapshot.project!, assetId, { detach });
        update((p) => withAssetRemoved(p, saved));
        buffers.invalidate(assetId);
        return true;
      } catch (e) {
        setError(describe('file', e));
        return false;
      } finally {
        setBusy(false);
      }
    },
    [buffers, repo, session, update],
  );

  const analyse = useCallback(
    async (assetId: string) => {
      const asset = session.snapshot.project?.assets.find((a) => a.id === assetId);
      if (!asset) return false;
      try {
        const buffer = await buffers.get(assetId);
        const info = analyseBuffer(buffer);
        update((p) => audioOps.setAssetAudioInfo(p, assetId, info));
        return true;
      } catch (e) {
        setError(describe(asset.name, e));
        return false;
      }
    },
    [buffers, session, update],
  );

  return {
    busy,
    error: error?.message ?? null,
    errorDetails: error?.details ?? [],
    clearError: useCallback(() => setError(null), []),
    importFiles,
    saveRecording,
    remove,
    analyse,
  };
}
